import { useLocation, useNavigate } from 'react-router-dom';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';

import { useLocationEventDetail } from '../../../presentation/hooks/useLocationEventDetail';
import { useQueryParams } from '@pocapan/shared/utils/useQueryParams';
import dDayCalculate from '@pocapan/shared/utils/dDayCalculate';
import { openNewTab } from '@pocapan/shared/utils/UIUtils';
import { getLocationEventEmoji, processedGoodsNames } from '@pocapan/shared/utils/EventUtils';

import StickyHeader from '../../../components/StickyHeader';

import { Button } from '@peacecover/component';
import { Calendar, Gift, Leader, Map as MapIcon, MapFill, Xmark } from '@peacecover/icon';
import { BaseLocationEvent } from '../../../domain/models/locations/BaseLocationEvent';

const AlreadyReportedEventPage = () => {
  const { event } = useLocation().state;
  const navigate = useNavigate();
  const { queryParams } = useQueryParams();
  const locationName = queryParams.get('locationName') || '';

  const { eventDetail } = useLocationEventDetail(event?._locationId, event?._eventId);
  const { colorMode } = useColorMode();

  // React Router state로 전달되며 클래스 프로토타입이 사라진 경우를 복구
  const baseEvent = Object.setPrototypeOf(event, BaseLocationEvent.prototype) as BaseLocationEvent;

  const handleNavigateToCreateEvent = () => {
    navigate(`/report-location/events/new?${queryParams.toString()}`);
  };

  const handleNavigateToEventDetail = () => {
    openNewTab(`/locations/${baseEvent.locationId}/events/${baseEvent.eventId}`);
  };

  const handleNavigateToEditEvent = () => {
    navigate(`/report-location/events/new?${queryParams.toString()}&isEdit=true`, {
      state: {
        event: baseEvent
      }
    });
  };

  return (
    <>
      <StickyHeader
        title={
          t('AlreadyReportedEventPage.pageTitle', {
            defaultValue: '제보하기'
          }) as string
        }
        alwaysShowTitle={true}
        showTitle={true}
        leftContent={
          <Xmark
            size={24}
            className="text-text-main-lv1"
            onClick={() => navigate(`/report-location/events?${queryParams.toString()}`)}
          />
        }
      />
      <div
        className="flex flex-col min-h-screen items-center pt-[calc(54px+env(safe-area-inset-top))] pb-[calc(128px+env(safe-area-inset-bottom))] bg-repeat"
        style={{
          backgroundImage: `url('/assets/backgrounds/report/report_complete_${colorMode === 'dark' ? 'dark' : 'light'}.png')`,
          backgroundRepeat: 'round'
        }}
      >
        <div className="flex flex-col gap-[16px] items-center mt-[57px]">
          <h1 className="text-[26px] font-bold text-text-main-lv1">
            {t('AlreadyReportedEventPage.title', {
              defaultValue: '정보가 다른가요?'
            })}
          </h1>
          <p className="text-[15px] text-text-main-lv3 leading-tight text-center whitespace-pre-wrap">
            {t('AlreadyReportedEventPage.description', {
              defaultValue: '정보가 잘못됐거나, 직접 주최한 경우엔 \n포카판에 알려주세요.'
            })}
          </p>
        </div>
        <EventCardWithLocation
          event={baseEvent}
          locationName={locationName}
          lotAddress={eventDetail?.location.lotAddress || ''}
        />
        <div className="fixed mb-[calc(10px+env(safe-area-inset-bottom))] flex flex-col gap-[8px] bottom-0 px-[20px] py-[10px] w-full">
          <Button
            size="large"
            className="w-full text-[17px] font-semibold"
            onClick={handleNavigateToEventDetail}
          >
            {t('AlreadyReportedEventPage.viewEvent', {
              defaultValue: '해당 {{spotsCategory}} 자세히보기',
              spotsCategory: t(`SpotsCategory.${baseEvent.category}.value`)
            })}
          </Button>
          <Button
            size="large"
            btnColor="secondary"
            className="w-full text-[17px] font-semibold"
            onClick={handleNavigateToEditEvent}
          >
            {t('AlreadyReportedEventPage.editEvent', {
              defaultValue: '정보 수정 요청하기'
            })}
          </Button>
          <Button
            size="large"
            btnColor="secondary"
            className="w-full text-[17px] font-semibold"
            onClick={handleNavigateToCreateEvent}
          >
            {t('AlreadyReportedEventPage.createEvent', {
              defaultValue: '다른 {{spotsCategory}} 등록하기',
              spotsCategory: t(`SpotsCategory.${baseEvent.category}.value`)
            })}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AlreadyReportedEventPage;

const EventCardWithLocation = ({
  event,
  locationName,
  lotAddress
}: {
  event: BaseLocationEvent;
  locationName: string;
  lotAddress: string;
}) => {
  return (
    <div className="rounded-[20px] bg-background-light dark:bg-background-dark dark:border dark:border-separator2 mt-[46px] w-[327px] shadow-[0_0_0_1px_rgba(0,0,0,0.01),0_0_14px_0_rgba(0,0,0,0.08)]">
      <div className="flex flex-col gap-[5px] px-[16px] py-[14px] border-b border-separator1 dark:border-separator2">
        <div className="flex gap-[2px] items-center">
          <MapFill size={14} className="text-on-theirty" />
          <p className="text-[17px] font-semibold text-text-main-lv2 leading-[20px]">
            {locationName}
          </p>
        </div>
        <p className="h-[16px] text-[13px] text-text-main-lv4 leading-[16px]">{lotAddress}</p>
      </div>
      <div className="p-[16px] flex items-center gap-[14px]">
        <img
          src={event.baseImageUrl}
          alt="event"
          className="w-[79px] h-[110px] rounded-[12px] object-cover"
        />
        <div className="flex flex-col gap-[6px]">
          <div className="font-semibold text-[16px] leading-[20px] text-text-main-lv1 line-clamp-2 mb-[4px] whitespace-pre-wrap">
            <span className="align-middle">{event.name}</span>
            <span className="text-[10px] px-[4px] py-[2px] rounded-[4px] bg-secondary-light dark:bg-secondary-dark text-on-secondary leading-none ml-[2px] align-middle inline-block">
              {dDayCalculate(event.startDate.toString(), event.endDate.toString())}
            </span>
          </div>
          <div className="text-[13px] leading-none text-text-main-lv4 flex items-center gap-[4px]">
            <img
              src={getLocationEventEmoji(event.category)}
              alt="birthday-cafe"
              className="w-[14px] h-[14px]"
            />
            {t(`SpotsCategory.${event.category}.value`)} ·{' '}
            <Leader size={14} className="text-text-sub-lv1" /> {event.organizerTwitterId}
          </div>
          <div className="flex items-center gap-[6px] leading-none">
            <Calendar size={14} className="text-text-sub-lv1" />
            <span className="text-[13px] text-text-main-lv4">
              {event.startDate.toLocaleDateString()}~{event.endDate.toLocaleDateString()}
            </span>
          </div>
          {event.goodsNames && (
            <div className="flex items-center gap-[6px] leading-none">
              <Gift size={14} className="text-text-sub-lv1" />
              <span className="text-[13px] text-text-main-lv4">
                {processedGoodsNames(event.goodsNames)}
              </span>
            </div>
          )}
          {event.category !== 'birthday_cafe' && (
            <div className="flex items-center gap-[6px] leading-none">
              <MapIcon size={14} className="text-text-sub-lv1" />
              <span className="text-[13px] text-text-main-lv4">{locationName}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};